import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { GraduationCap, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import portalApi, { portalStorage, formatPortalError } from "./portalApi";
import usePageMeta from "../../lib/usePageMeta";

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]";

const COURSE_META = {
  "CTA-001": {
    code: "CTA-001",
    title: "CTA-001 — Grief in the Workplace",
    duration: "about 60 minutes",
    ctaLabel: "Create Account & Start CTA-001",
    successToast: "Welcome! You're enrolled in CTA-001.",
    afterRedirect: "/academy/portal/dashboard",
    disclaimer: "CTA-001 is introductory educational training. Completion does not confer professional licensure, clinical certification, accredited continuing-education credit, or authority to provide mental-health diagnosis or clinical treatment.",
  },
  "HOSP-001": {
    code: "HOSP-001",
    title: "HOSP-001 — See the Person First",
    duration: "approximately 35–45 minutes",
    ctaLabel: "Create Account & Start HOSP-001",
    successToast: "Welcome! You're enrolled in HOSP-001.",
    afterRedirect: "/academy/portal/hosp-001/learn",
    disclaimer: "HOSP-001 is introductory educational training. Completion does not confer professional licensure, clinical certification, accredited continuing-education credit, hospice certification, or authority to provide medical, mental-health, or clinical treatment.",
  },
};

function PortalShellHeader() {
  return (
    <nav className="bg-white border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/academy/portal" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#4A6FA5] text-white flex items-center justify-center"><GraduationCap className="w-5 h-5" strokeWidth={1.75}/></div>
          <div>
            <div className="font-heading text-slate-900 text-sm font-semibold leading-tight">CCCHAPS</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 leading-tight">Chaplain Training Academy</div>
          </div>
        </Link>
      </div>
    </nav>
  );
}

export function PortalRegister() {
  const [searchParams] = useSearchParams();
  const courseParam = (searchParams.get("course") || "").toUpperCase();
  const meta = COURSE_META[courseParam] || COURSE_META["CTA-001"];
  usePageMeta({ title: `Create your Academy account · ${meta.code} | CCCHAPS`, description: "" });
  const nav = useNavigate();
  const [f, setF] = useState({ first_name:"", last_name:"", email:"", password:"", phone:"", organization:"", chaplain_role:"", accommodation_request:"" });
  const [required, setRequired] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const u = k => e => setF(s=>({...s,[k]:e.target.value}));
  const submit = async e => {
    e.preventDefault();
    if (!required) return toast.error("Please confirm you agree to receive course communications.");
    if (f.password.length < 8) return toast.error("Password must be at least 8 characters.");
    setBusy(true);
    try {
      const r = await portalApi.post("/portal/auth/register", { ...f, course_comms_consent: required, marketing_optin: marketing, course_code: meta.code });
      portalStorage.setToken(r.data.access_token);
      portalStorage.setLearner(r.data.learner);
      toast.success(meta.successToast);
      nav(meta.afterRedirect);
    } catch (err) { toast.error(formatPortalError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-[#FDFCF6] min-h-screen">
      <PortalShellHeader/>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12" data-testid="portal-register-page">
        <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold" data-testid="reg-course-eyebrow">{meta.code} · Registration</p>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">Create your Academy account</h1>
        <p className="mt-3 text-slate-600">You'll be enrolled in <strong>{meta.title}</strong>. It's free and takes {meta.duration}.</p>
        <form onSubmit={submit} data-testid="portal-register-form" className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-4 shadow-sm">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="First name" req><input required data-testid="reg-first-name" value={f.first_name} onChange={u("first_name")} className={inputCls}/></Field>
            <Field label="Last name" req><input required data-testid="reg-last-name" value={f.last_name} onChange={u("last_name")} className={inputCls}/></Field>
          </div>
          <Field label="Email" req><input type="email" required data-testid="reg-email" value={f.email} onChange={u("email")} className={inputCls}/></Field>
          <Field label="Password (min. 8 characters)" req><input type="password" required minLength={8} data-testid="reg-password" value={f.password} onChange={u("password")} className={inputCls}/></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Phone (optional)"><input data-testid="reg-phone" value={f.phone} onChange={u("phone")} className={inputCls}/></Field>
            <Field label="Organization (optional)"><input data-testid="reg-organization" value={f.organization} onChange={u("organization")} className={inputCls}/></Field>
          </div>
          <Field label="Chaplain role or area of interest (optional)"><input data-testid="reg-role" value={f.chaplain_role} onChange={u("chaplain_role")} className={inputCls} placeholder="e.g. workplace chaplain, volunteer, aspiring, HR/EAP"/></Field>
          <Field label="Accessibility or accommodation request (optional)"><textarea rows={2} data-testid="reg-accommodation" value={f.accommodation_request} onChange={u("accommodation_request")} className={`${inputCls} resize-none`}/></Field>

          <label className="flex items-start gap-3 text-sm text-slate-700 pt-2">
            <input type="checkbox" checked={required} onChange={e=>setRequired(e.target.checked)} data-testid="reg-required-consent" className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#4A6FA5]"/>
            <span>I agree to receive communications necessary for this course. <span className="text-[#4A6FA5]">*</span></span>
          </label>
          <label className="flex items-start gap-3 text-sm text-slate-700">
            <input type="checkbox" checked={marketing} onChange={e=>setMarketing(e.target.checked)} data-testid="reg-marketing" className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#4A6FA5]"/>
            <span>I would like to receive future CCCHAPS news and training announcements.</span>
          </label>

          <button type="submit" disabled={busy} data-testid="reg-submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] disabled:opacity-60">
            {busy ? "Creating account…" : (<>{meta.ctaLabel} <ArrowRight className="w-4 h-4"/></>)}
          </button>
          <p className="text-xs text-slate-500 text-center">Already registered? <Link to={`/academy/portal/login?course=${meta.code}`} className="text-[#4A6FA5] hover:underline">Sign in</Link></p>
          <p className="text-[11px] text-slate-500 leading-relaxed border-t border-slate-100 pt-3" data-testid="reg-disclaimer">
            {meta.disclaimer}
          </p>
        </form>
      </div>
    </div>
  );
}

export function PortalLogin() {
  const [searchParams] = useSearchParams();
  const courseParam = (searchParams.get("course") || "").toUpperCase();
  const meta = COURSE_META[courseParam] || null;
  usePageMeta({ title: "Sign in | CCCHAPS Academy", description: "" });
  const nav = useNavigate();
  const [email,setEmail]=useState(""); const [password,setPassword]=useState(""); const [busy,setBusy]=useState(false);
  const submit = async e => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await portalApi.post("/portal/auth/login", { email, password });
      portalStorage.setToken(r.data.access_token); portalStorage.setLearner(r.data.learner);
      // If course was specified, enroll (idempotent) and redirect there
      if (meta) {
        try { await portalApi.post("/portal/enroll", { course_code: meta.code }); } catch (_) { /* ignore */ }
        nav(meta.afterRedirect);
      } else {
        nav("/academy/portal/dashboard");
      }
    } catch (err) { toast.error(formatPortalError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-[#FDFCF6] min-h-screen">
      <PortalShellHeader/>
      <div className="max-w-md mx-auto px-4 sm:px-6 py-16" data-testid="portal-login-page">
        <h1 className="font-heading text-3xl tracking-tight text-slate-900">Welcome back</h1>
        <p className="mt-2 text-slate-600 text-sm">{meta ? `Sign in to enroll in ${meta.title}.` : "Sign in to continue your course."}</p>
        <form onSubmit={submit} data-testid="portal-login-form" className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 space-y-4 shadow-sm">
          <Field label="Email" req><input type="email" required data-testid="login-email" value={email} onChange={e=>setEmail(e.target.value)} className={inputCls}/></Field>
          <Field label="Password" req><input type="password" required data-testid="login-password" value={password} onChange={e=>setPassword(e.target.value)} className={inputCls}/></Field>
          <button type="submit" disabled={busy} data-testid="login-submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] disabled:opacity-60">
            {busy ? "Signing in…" : "Sign In"}
          </button>
          <p className="text-xs text-slate-500 text-center">No account yet? <Link to={`/academy/portal/register${meta ? `?course=${meta.code}` : ""}`} className="text-[#4A6FA5] hover:underline">Create one</Link></p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, req, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">{label} {req && <span className="text-[#4A6FA5]">*</span>}</span>
      {children}
    </label>
  );
}
